"use client"

import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Check } from "lucide-react"
import { FAQ } from "@/components/faq"
import { useLanguage } from "@/contexts/language-context"

export default function PricingPage() {
  const { t } = useLanguage()

  return (
    <div className="flex flex-col">
      <section className="w-full py-12 md:py-24 lg:py-32 bg-[#1a1310]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col items-center justify-center space-y-4 text-center">
            <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">{t("choosePlan")}</h1>
            <p className="max-w-[700px] text-muted-foreground md:text-xl">{t("pricingDesc")}</p>
            <div className="flex items-center space-x-2">
              <span>{t("monthly")}</span>
              <Switch id="billing-switch" />
              <span>
                {t("yearly")}{" "}
                <Badge variant="outline" className="ml-1 bg-amber-500/10 text-amber-500 border-amber-500/20">
                  {t("saveTwoMonths")}
                </Badge>
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {/* Basic Plan */}
            <div className="flex flex-col p-6 bg-muted/5 border rounded-lg shadow-sm">
              <div className="space-y-2">
                <h3 className="text-xl font-bold">{t("advanced")}</h3>
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold">$10</span>
                  <span className="text-muted-foreground ml-1">/{t("monthly")}</span>
                </div>
                <p className="text-sm text-muted-foreground">{t("personalUse")}</p>
              </div>
              <div className="flex flex-col mt-6 space-y-4">
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("unlimitedGeneration")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("fastGeneration")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("noAds")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("noWatermark")}</span>
                </div>
              </div>
              <Button className="mt-6" variant="outline">
                {t("upgradeToAdvanced")}
              </Button>
            </div>

            {/* Ultimate Plan */}
            <div className="flex flex-col p-6 bg-amber-500/10 border border-amber-500/20 rounded-lg shadow-sm relative">
              <Badge className="absolute -top-2 -right-2 bg-amber-500">{t("mostPopular")}</Badge>
              <div className="space-y-2">
                <h3 className="text-xl font-bold">{t("ultimate")}</h3>
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold">$20</span>
                  <span className="text-muted-foreground ml-1">/{t("monthly")}</span>
                </div>
                <p className="text-sm text-muted-foreground">{t("professionalCreators")}</p>
              </div>
              <div className="flex flex-col mt-6 space-y-4">
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("unlimitedGeneration")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("ultraFastGeneration")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("noAds")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("noWatermark")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("hdResolution")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("privateGeneration")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("advancedModels")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("earlyAccess")}</span>
                </div>
              </div>
              <Button className="mt-6 bg-amber-500 hover:bg-amber-600">{t("upgradeToUltimate")}</Button>
            </div>

            {/* Enterprise Plan */}
            <div className="flex flex-col p-6 bg-muted/5 border rounded-lg shadow-sm">
              <div className="space-y-2">
                <h3 className="text-xl font-bold">{t("enterprise")}</h3>
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold">{t("custom")}</span>
                </div>
                <p className="text-sm text-muted-foreground">{t("teamsAndEnterprises")}</p>
              </div>
              <div className="flex flex-col mt-6 space-y-4">
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("customSolutions")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("dedicatedSupport")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("teamIntegration")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("apiAccess")}</span>
                </div>
                <div className="flex items-start">
                  <Check className="mr-2 h-5 w-5 text-green-500" />
                  <span>{t("highVolume")}</span>
                </div>
              </div>
              <Button className="mt-6" variant="outline">
                {t("contactUs")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ />
    </div>
  )
}
